import { useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";
import { LazyNetwork } from "@/components/fx/LazyNetwork";
import { SectionCta } from "@/components/SectionCta";

const STEPS = [
  { t: "Capiamo chi ti serve davvero", d: "Chiamata e domande scritte su ruolo, azienda, squadra, carattere." },
  { t: "Giriamo foto e video da te", d: "Contenuti veri in azienda per gli annunci: è quello che convince i bravi a farsi avanti." },
  { t: "Andiamo a cercare i candidati", d: "Campagne mirate dove le persone giuste ci sono anche se non cercano lavoro. In parallelo attingiamo al nostro database candidati e cerchiamo direttamente i profili giusti. Domande filtro per la prima scrematura." },
  { t: "CV e video presentazione", d: "Chi si mette in gioco col video guadagna posizioni." },
  { t: "Test attitudinale su misura", d: "Costruito sulla tua attività: affidabilità, tenuta, attitudine, lavoro in squadra." },
  { t: "Colloquio con noi, poi con te", d: "Chi supera tutto fa un colloquio con il nostro team. Poi ti presentiamo una rosa ristretta di profili (es. 3) con la scheda completa e organizziamo l'incontro con te. Tu scegli e assumi.", hl: true },
];

const StepCard = ({ s, i }) => (
  <article
    data-step
    data-testid={`metodo-step-${i + 1}`}
    className={`relative flex shrink-0 flex-col rounded-2xl border p-7 backdrop-blur-md sm:p-8 lg:min-h-[300px] lg:w-[400px] xl:w-[440px] ${
      s.hl ? "border-[#EF9F27]/70 bg-[#EF9F27]/[0.08] shadow-[0_0_80px_-30px_rgba(239,159,39,0.6)]" : "border-white/12 bg-white/[0.04]"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className={`font-mono-data text-5xl font-medium leading-none tracking-tight lg:text-6xl ${s.hl ? "text-[#EF9F27]" : "text-[#BCE3D6]"}`}>
        {String(i + 1).padStart(2, "0")}
      </span>
      {s.hl ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EF9F27] px-3 py-1 font-mono-data text-[11px] uppercase tracking-[0.14em] text-[#2C2C2A]">
          <Sparkles size={12} /> Match
        </span>
      ) : (
        <span className="h-2 w-2 rounded-full bg-[#BCE3D6]/60" />
      )}
    </div>
    <h3 className="mt-10 text-[22px] leading-tight tracking-[-0.02em] text-white lg:mt-auto lg:pt-10 lg:text-2xl">{s.t}</h3>
    <p className="mt-3 text-[15px] leading-relaxed text-white/75">{s.d}</p>
  </article>
);

export const Metodo = ({ steps = STEPS, eyebrow = "Il metodo · Come lavoriamo", title = "Dalla prima chiamata alla persona seduta in azienda.", finalLabel = "Cosa fai tu:", finalText = "una chiamata iniziale, qualche ora per lo shooting, gli incontri finali con i candidati migliori.", cta = null }) => {
  const pinRef = useRef(null);
  const trackRef = useRef(null);
  const barRef = useRef(null);
  const listRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    let mm, cancelled = false;
    (async () => {
      const [{ gsap }, { ScrollTrigger }] = await Promise.all([import("gsap"), import("gsap/ScrollTrigger")]);
      if (cancelled) return;
      gsap.registerPlugin(ScrollTrigger);
      mm = gsap.matchMedia();
      mm.add(
        { desk: "(min-width: 1024px) and (prefers-reduced-motion: no-preference)", mob: "(max-width: 1023px) and (prefers-reduced-motion: no-preference)" },
        (c) => {
          const cards = gsap.utils.toArray(trackRef.current.querySelectorAll("[data-step]"));
          if (c.conditions.desk) {
            const track = trackRef.current;
            const dist = () => Math.max(0, track.scrollWidth - window.innerWidth);
            const tween = gsap.to(track, {
              x: () => -dist(),
              ease: "none",
              scrollTrigger: { trigger: pinRef.current, start: "top top", end: () => "+=" + (dist() + window.innerHeight * 0.4), pin: true, scrub: 0.8, invalidateOnRefresh: true, anticipatePin: 1 },
            });
            gsap.fromTo(barRef.current, { scaleX: 0 }, { scaleX: 1, ease: "none", scrollTrigger: { trigger: pinRef.current, start: "top top", end: () => "+=" + (dist() + window.innerHeight * 0.4), scrub: 0.8, invalidateOnRefresh: true } });
            cards.forEach((card, i) => {
              if (i < 2) {
                gsap.from(card, { opacity: 0, y: 60, duration: 1, delay: i * 0.12, ease: "power3.out", scrollTrigger: { trigger: pinRef.current, start: "top 70%" } });
              } else {
                gsap.from(card, { opacity: 0.08, y: 50, scale: 0.94, ease: "power2.out", scrollTrigger: { trigger: card, containerAnimation: tween, start: "left 95%", end: "left 60%", scrub: true } });
              }
            });
          } else {
            gsap.fromTo(lineRef.current, { scaleY: 0 }, { scaleY: 1, ease: "none", scrollTrigger: { trigger: listRef.current, start: "top 70%", end: "bottom 70%", scrub: true } });
            cards.forEach((card) => gsap.from(card, { opacity: 0, y: 40, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: card, start: "top 85%" } }));
          }
        }
      );
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => ScrollTrigger.refresh());
      setTimeout(() => ScrollTrigger.refresh(), 800);
    })();
    return () => {
      cancelled = true;
      if (mm) mm.revert();
    };
  }, []);

  return (
    <section id="metodo" data-theme="dark" className="rl-section relative bg-[#13352F] text-white" data-testid="metodo-section">
      <div ref={pinRef} className="rl-grain relative flex flex-col overflow-hidden py-24 lg:h-screen lg:min-h-[720px] lg:justify-center lg:py-0">
        <LazyNetwork density={14000} amberRatio={0.07} />
        <div className="rl-container relative">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="rl-eyebrow text-[#BCE3D6]">{eyebrow}</p>
              <h2 className="rl-h2 mt-6 max-w-[900px] !text-[clamp(2rem,4.2vw,3.8rem)]" data-testid="metodo-title">
                {title}
              </h2>
            </div>
            <div className="hidden w-[260px] shrink-0 lg:block">
              <div className="flex justify-between font-mono-data text-[11px] uppercase tracking-[0.16em] text-[#BCE3D6]/80">
                <span>01</span><span>06</span>
              </div>
              <div className="mt-2 h-[2px] w-full overflow-hidden rounded bg-white/10">
                <div ref={barRef} className="h-full w-full origin-left bg-[#BCE3D6]" style={{ transform: "scaleX(0)" }} data-testid="metodo-progress" />
              </div>
            </div>
          </div>
        </div>
        <div ref={listRef} className="relative mt-12 lg:mt-14">
          <div className="absolute bottom-6 left-[29px] top-6 w-px bg-white/10 lg:hidden" aria-hidden="true">
            <div ref={lineRef} className="h-full w-full origin-top bg-[#BCE3D6]" />
          </div>
          <div ref={trackRef} className="flex flex-col gap-5 pl-[52px] pr-5 lg:w-max lg:flex-row lg:gap-6 lg:pl-[max(64px,calc((100vw-1360px)/2+64px))] lg:pr-[12vw]" data-testid="metodo-track">
            {steps.map((s, i) => <StepCard key={s.t} s={s} i={i} />)}
          </div>
        </div>
        <div className="rl-container relative mt-12 lg:mt-12">
          {finalText && (
            <p className="max-w-[760px] text-[16px] leading-relaxed text-white/85 sm:text-lg" data-testid="metodo-final-line">
              <span className="font-mono-data mr-2 text-[13px] uppercase tracking-[0.14em] text-[#BCE3D6]">{finalLabel}</span>
              {finalText}
            </p>
          )}
          {cta && <SectionCta label={cta} testId="metodo-cta" />}
        </div>
      </div>
    </section>
  );
};
